import { useCallback, useEffect, useRef, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import type { ICity } from 'country-state-city';

import {
	mutateBpicLocationFormatToInputFormat,
	mutateCityToBpicLocationFormat,
	mutateFreeTextToBpicLocationFormat,
} from '@/lib/utils';

type Csc = typeof import('country-state-city');

interface CityOption {
	label: string;
	city?: ICity;
	customOption?: boolean;
}

interface LocationTypeaheadProps {
	id?: string;
	value: string;
	onChange: (locationJson: string) => void;
	disabled?: boolean;
}

const MAX_RESULTS = 50;

// Load the dataset once per session; the dynamic import keeps it in its own chunk
// so it only downloads when a location field actually renders.
let cscPromise: Promise<Csc> | null = null;
const loadCsc = (): Promise<Csc> => {
	if (!cscPromise) {
		cscPromise = import('country-state-city');
	}

	return cscPromise;
};

const buildLabel = (csc: Csc, city: ICity): string => {
	const country = csc.Country.getCountryByCode(city.countryCode);
	const state = city.stateCode
		? csc.State.getStateByCodeAndCountry(city.stateCode, city.countryCode)
		: undefined;

	return [city.name, state?.name, country?.name].filter(Boolean).join(', ');
};

const buildOptions = (csc: Csc, cities: ICity[], query: string): CityOption[] => {
	const normalizedQuery = query.trim().toLowerCase();
	if (!normalizedQuery) {
		return [];
	}

	const results: CityOption[] = [];
	for (let i = 0; i < cities.length && results.length < MAX_RESULTS; i++) {
		if (cities[i].name.toLowerCase().startsWith(normalizedQuery)) {
			results.push({ label: buildLabel(csc, cities[i]), city: cities[i] });
		}
	}

	return results;
};

export const LocationTypeahead = ({ id, value, onChange, disabled }: LocationTypeaheadProps) => {
	const cscRef = useRef<Csc | null>(null);
	const citiesRef = useRef<ICity[] | null>(null);
	const [options, setOptions] = useState<CityOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const initialLabel = value ? mutateBpicLocationFormatToInputFormat(value) : '';
	const [selected, setSelected] = useState<CityOption[]>(initialLabel ? [{ label: initialLabel }] : []);

	// Prefetch the dataset as soon as the field is on screen, so it is ready before
	// the user starts typing.
	useEffect(() => {
		let active = true;

		loadCsc().then((csc) => {
			if (!active) {
				return;
			}

			cscRef.current = csc;
			citiesRef.current = csc.City.getAllCities();
		});

		return () => {
			active = false;
		};
	}, []);

	const handleSearch = useCallback((query: string) => {
		if (cscRef.current && citiesRef.current) {
			setOptions(buildOptions(cscRef.current, citiesRef.current, query));
			return;
		}

		// Dataset not loaded yet — load, then run the search.
		setIsLoading(true);
		loadCsc().then((csc) => {
			cscRef.current = csc;
			citiesRef.current = csc.City.getAllCities();
			setOptions(buildOptions(csc, citiesRef.current, query));
			setIsLoading(false);
		});
	}, []);

	const handleChange = (selectedOptions: CityOption[]) => {
		setSelected(selectedOptions);

		const option = selectedOptions[0];
		if (!option) {
			onChange('');
			return;
		}

		if (option.city && cscRef.current) {
			const { Country, State } = cscRef.current;
			const country = Country.getCountryByCode(option.city.countryCode);
			const state = option.city.stateCode
				? State.getStateByCodeAndCountry(option.city.stateCode, option.city.countryCode)
				: undefined;
			onChange(mutateCityToBpicLocationFormat(option.city, country, state));
		} else {
			// Free-typed entry (allowNew) — keep whatever the user wrote.
			onChange(mutateFreeTextToBpicLocationFormat(option.label));
		}
	};

	return (
		<AsyncTypeahead
			id={id ?? 'location-typeahead'}
			isLoading={isLoading}
			minLength={2}
			delay={200}
			options={options}
			labelKey="label"
			filterBy={() => true}
			onSearch={handleSearch}
			onChange={(selectedOptions) => handleChange(selectedOptions as CityOption[])}
			selected={selected}
			allowNew
			newSelectionPrefix="Use: "
			placeholder="Start typing a city..."
			disabled={disabled}
			useCache={false}
		/>
	);
};
